import { ComponentRef, EventEmitter, inject, Injectable, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import { CacheStorageAdapter, Query, QueryColumn, QueryObjectsModel } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import { ParameterHelper, ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { AbcContainerService, AbcWindowComponent, IWindow, WindowStyle } from '@barthauer/abc/ui-components';
import { from, lastValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, takeUntil, tap, toArray } from 'rxjs/operators';
import { ResultWorkGeneration } from '../../create-works-wizard/models/helpers';
import { MtBase } from '../../create-works-wizard/models/MtBase';
import { MtWork } from '../../create-works-wizard/models/MtWork';
import { MtWorkGenerationInfo } from '../../create-works-wizard/models/MtWorkGenerationInfo';
import { MtWorkTypeSimple } from '../../create-works-wizard/models/MtWorkType';
import { WorkGeneratorMode, WorkState, WorkWizardOptions } from '../../create-works-wizard/models/WorkWizardOptions';
import { WorkService } from '../../create-works-wizard/services/work.service';
import { CreateImmediateMeasureWizardComponent } from '../create-immediate-measure-wizard.component';
import { AppService } from '@barthauer/abc/maintenance/opera/core';
import { UserService } from '@barthauer/basys/core/authentication';

@Injectable({
  providedIn: 'root',
})
export class ImWorkService {
  private unsubscribe$ = new Subject<void>();
  @Output() imWorkGenerationSucceeded = new EventEmitter<ResultWorkGeneration>();
  @Output() imWorkGenerationFailed = new EventEmitter();
  @Output() onImWorkCreated = new EventEmitter<{ workId: string; show: boolean }>();

  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) abcWindow: ViewContainerRef;

  public static readonly AdditionalWorkTypeFilter: string = `Exists(Properties[Designation='Sofortmassnahme' And NOT ToLower(CaseNull(TextValue, '0')) In ('0', 'false')])`;

  public getWorkTypesWithDateFilter: (entity: DataEntity) => Promise<MtWorkTypeSimple[]>;
  public generateWorksForEntity: (
    entity: DataEntity,
    options: WorkWizardOptions,
  ) => Promise<{ generatedWorks: MtWork[]; excludedObjects: MtBase[] }>;
  private windowInstance: AbcWindowComponent;
  wizardComponentRef: ComponentRef<any>;
  public resAsync: Promise<ResourceSet>;
  private currentMtObj?: MtBase;
  public persistPendingChanges: (category: string, errorCallback?: (err: any) => void) => void;

  constructor(
    private workService: WorkService,
    private abcContainer: AbcContainerService,
    private resourceService: ResourceManagerService,
    private abcCore: AbcCore,
    private userService: UserService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('opera-wizards');
    this.workService.generationFinished
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((worksgeneratedResult) => this.onWorkGenerationSucceeded(worksgeneratedResult));
    this.workService.generationFailed
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((err) => this.onWorkGenerationFailed(err));
  }

  onWorkGenerationFailed(err: any): void {
    this.imWorkGenerationFailed.emit(err);
  }

  onWorkGenerationSucceeded(worksgeneratedResult: ResultWorkGeneration): void {
    this.imWorkGenerationSucceeded.emit(worksgeneratedResult);
  }

  generateImWork(entity: DataEntity, workType: MtWorkTypeSimple, orderId: string = '') {
    if (CacheStorageAdapter.useOfflineMode && this.generateWorksForEntity) {
      const workWizardOptions: WorkWizardOptions = this.createWorkOptions(undefined, workType, orderId);
      from(this.generateWorksForEntity(entity, workWizardOptions)).subscribe(
        (_) => {
          let result: ResultWorkGeneration = {
            generatedWorks: _?.generatedWorks,
            excludedObjects: _?.excludedObjects,
          };
          this.workService.generationFinished.emit(result);
        },
        (err) => this.imWorkGenerationFailed.emit(err),
      );
      return;
    }

    this.workService.ensureMtObjectFromEntity(entity).subscribe(
      (mtObj) => {
        if (mtObj) {
          const workWizardOptions: WorkWizardOptions = this.createWorkOptions(mtObj, workType, orderId);
          this.workService.generateWorks(
            workWizardOptions.objectInfos,
            workWizardOptions.state ?? WorkState.anstehend,
            workWizardOptions.workTypeId ?? '',
            workWizardOptions.orderId ?? '',
            workWizardOptions.source ?? '',
          );
        } else {
          this.imWorkGenerationFailed.emit(new Error('Anlage-Objekt nicht vorhanden!')); //localize
        }
      },
      (err) => this.imWorkGenerationFailed.emit(err),
    );
  }

  public ensureMtObjectFromEntity(entity: DataEntity): Promise<MtBase | undefined> {
    return this.workService.ensureMtObjectFromEntity(entity).toPromise();
  }

  private createWorkOptions(
    mtObj: MtBase | undefined,
    workType: MtWorkTypeSimple,
    orderId: string = '',
  ): WorkWizardOptions {
    const curDate = new Date();
    let ret: WorkWizardOptions = new WorkWizardOptions();
    if (mtObj?.Id) {
      let infoObject: MtWorkGenerationInfo = new MtWorkGenerationInfo();
      infoObject.MtObjectId = mtObj.Id;

      infoObject.ScheduledWorkExecDate = curDate;
      infoObject.EarliestWorkExecDate = curDate;
      infoObject.LatestWorkExecDate = curDate;

      ret.objectInfos = [infoObject];
    }
    ret.state = WorkState.anstehend;
    ret.workGeneratorMode = WorkGeneratorMode.manual;
    ret.workTypeId = workType.Id;
    ret.scheduledExecutionDate = curDate;
    ret.executeLatest = curDate;
    ret.executeEarliest = curDate;
    if (orderId) ret.orderId = orderId;
    ret.source = '1'; //ImmediateMeasure;

    return ret;
  }

  queryRelevantWorkTypesWithDateFilter(
    baseObjType: string | number,
    entity: DataEntity,
  ): Observable<MtWorkTypeSimple[]> {
    return from(this.queryRelevantWorkTypesWithDateFilterCore(baseObjType, entity));
  }

  private async queryRelevantWorkTypesWithDateFilterCore(
    baseObjType: string | number,
    entity: DataEntity,
  ): Promise<MtWorkTypeSimple[]> {
    if (CacheStorageAdapter.useOfflineMode && this.getWorkTypesWithDateFilter) {
      return await this.getWorkTypesWithDateFilter(entity);
    }

    await lastValueFrom(this.workService.ensureMtObjectFromEntity(entity));
    const mtObj = await lastValueFrom(this.workService.queryMtObjectFromEntity(entity));
    this.currentMtObj = mtObj;
    const otView = await lastValueFrom(this.workService.getObjectTypeValue(baseObjType));
    const filter = ImWorkService.AdditionalWorkTypeFilter;
    const oPath: string =
      (await AppService.getWorkTypeFilterCore(this.userService)) +
      ` And (${filter} OR NOT EXISTS(Type(MtWorkType)[${filter}]) AND Exists(Works.Object[GeObjectType=${otView.value.toString()}])) And Not(Exists(Works[ParentId=$ID And ScheduledExecutionDate = $PDATE]))`;

    return ConvertHelper.arrayBufferToObjectArray<MtWorkTypeSimple>(
      await lastValueFrom(
        this.workService.createDropDownListQueryWithObjectTypeDefinition('MtWorkType', oPath, undefined, undefined, [
          new ParameterValue('ID', mtObj?.Id),
          new ParameterValue('PDATE', ParameterHelper.getDateValue(new Date())),
        ]),
      ),
    );
  }

  public queryWorkTypeObjectDefinitions(workTypes: MtWorkTypeSimple[]): Observable<MtWorkTypeSimple[]> {
    if (CacheStorageAdapter.useOfflineMode) {
      return of(workTypes);
    } else {
      const objId = this.currentMtObj?.Id;

      if (!objId) {
        return of([]);
      }

      return from(workTypes).pipe(
        mergeMap((wt) => {
          const def = wt.objectTypeDefinition?.trim();
          if (!def) {
            return of(wt);
          }
          return this.queryWorkTypesForImmediateMeasure(objId, def).pipe(
            filter((isValid) => isValid),
            map(() => wt),
          );
        }),
        toArray(),
      );
    }
  }

  public queryWorkTypesForImmediateMeasure(objId: string, objectDefinition: string): Observable<boolean> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'MtObject';
    const opath = `(Id In (${objectDefinition}.Id) Or IdMasterObject In (${objectDefinition}.Id))
                   And ParentId=$CurrentMunicipalityVariantId
                   And Id = '${objId}'`;

    query.OPath = opath;

    const columns: QueryColumn[] = [];
    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'ObjectType' });

    query.Columns = columns;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';

    queryDocument.ObjectQueries = [query];

    return this.abcCore.data.executeReadObjectsQuery(queryDocument).pipe(
      map((array) => {
        const result = ConvertHelper.arrayBufferToObjectArray<MtBase>(array);
        return result;
      }),
      map((objects: MtBase[] | null | undefined) => {
        return !!objects && objects.length > 0;
      }),

      catchError((err) => {
        console.error('queryWorkTypesForImmediateMeasure error:', err);
        console.log('The Opath for the ObjectDefinition is wrong and the worktype is evaluated as valid');
        return of(true);
      }),
    );
  }

  imWorkWizard: CreateImmediateMeasureWizardComponent;
  public async showImWorkDialog(entities: DataEntity[], orderId: string) {
    const style: WindowStyle = {
      height: '380',
      width: '500',
      minHeight: '380',
      minWidth: '500',
      shading: true,
      resizable: false,
      position: { my: 'top center', at: 'top center', offset: '35 10' },
    };

    const WindowAttr: IWindow = {
      id: 'imWorkWizard',
      label: (await this.resAsync).get('ImmediateWorkDialogLabel'),
      maximizeButton: true,
      minimizeButton: true,
      component: CreateImmediateMeasureWizardComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
    this.wizardComponentRef = window.componentRef;
    this.windowInstance = <AbcWindowComponent>window.componentRef.instance;
    this.windowInstance.createComponentInstance().subscribe((activeComponent) => {
      const w = activeComponent.openWindow();
      const dialogInstance = w.instance;
      const typedDialogInstance = <CreateImmediateMeasureWizardComponent>dialogInstance;
      typedDialogInstance?.init(entities, orderId);
      this.windowInstance.onAfterOpened.subscribe(() => this.onWizardDialogOpened(dialogInstance));
      this.windowInstance.onAfterClosed.subscribe(() => this.onWizardDialogClosed());
    });
  }

  onWizardDialogOpened(dialogInstance: any) {
    this.imWorkWizard = dialogInstance;
    this.imWorkWizard?.closeWizardDialog.subscribe(() => this.closeWizardContainer());
    this.imWorkWizard?.showWork.subscribe((e) => this.raiseShowWorkById(e));
  }

  raiseShowWorkById(e: { workId: string; show: boolean }): void {
    this.onImWorkCreated.emit(e);
  }

  onWizardDialogClosed(): void {
    if (this.wizardComponentRef) this.wizardComponentRef.destroy();
  }

  closeWizardContainer(): void {
    if (this.windowInstance) {
      this.windowInstance.closeDialog();
      if (this.wizardComponentRef) this.wizardComponentRef.destroy();
    }
  }
}
